import React, { useCallback, useEffect, useRef } from 'react';
import style from './style.module.scss';
import { IProps, IHandlers } from './types';
import { renderIf } from 'app/services/reactUtils.service';
import classNames from 'classnames';
import { Typography } from '@material-ui/core';
import { StatusService } from 'external-services/status-service';
import { bookingStatusType, StatusCharacterType } from 'shared-types/index';
import StatusConfigService from 'external-services/status-config-service';
import { displayActions } from 'booking-creator/reducers/display/slice';

const NS = 'StatusSymbol';

export default function StatusSymbol({
  status,
  bookingType,
  paymentDetails,
  includeName,
  includeType,
  typeOverride,
  getCurrentStatusColor
}: IProps & IHandlers) {

  const isPaymentPending = status?.statusType === bookingStatusType?.pendingPayment;
  const isSeated = status?.statusType === bookingStatusType?.seated;
  const isOrdered = status?.name?.toLowerCase() === 'ordered';

  const { isSBPaymentPending, isSBPaymentUnconfirmed } = StatusService.getStandbyPaymentPendingState(status?.statusType, paymentDetails, bookingType);

  const { char1, char2 } = StatusService.getStatusCodeCharacters(status, paymentDetails, bookingType);

  useEffect(() => {
    if (getCurrentStatusColor) {
      const currentColorInfo = StatusConfigService.getStatusColorBasedOnStatusType(status?.statusType, isSeated, isOrdered, isPaymentPending, status?.statusColor)
      getCurrentStatusColor({ ...currentColorInfo, statusId: status?._id })
    }
  }, [])

  const getStatusColorBasedOnStatusType = useCallback(() => {
    return StatusConfigService.getStatusColorBasedOnStatusType(status?.statusType, isSeated, isOrdered, isPaymentPending, status?.statusColor)
  }, [status?.statusType, isSeated, isOrdered, isPaymentPending, status?.statusColor]);

  return (
    <div className={classNames({
      [style.root]: true,
    })} aria-label="Booking Status" data-ns="status-symbol" >

      <span
        style={getStatusColorBasedOnStatusType()}
        className={style.statusText}
        data-testid="characterWrap">
        {/* Character 1 */}
        {renderIf(!char1.large, () => <>{char1.text}</>)}
        {renderIf(char1.large && char1.type === StatusCharacterType.typeA, () => (
          <span data-testid="char1A" className={style.largeStatusSymbolA}>{char1.text}</span>
        ))}
        {renderIf(char1.large && char1.type === StatusCharacterType.typeB, () => (
          <span data-testid="char1B" className={style.largeStatusSymbolB}>{char1.text}</span>
        ))}

        {/* Character 2 */}
        {renderIf(!char2.large, () => <>{char2.text}</>)}
        {renderIf(char2.large && char2.type === StatusCharacterType.typeA, () => (
          <span data-testid="char2A" className={style.largeStatusSymbolA}>{char2.text}</span>
        ))}
        {renderIf(char2.large && char2.type === StatusCharacterType.typeB, () => (
          <span data-testid="char2B" className={style.largeStatusSymbolB}>{char2.text}</span>
        ))}
      </span>

      {renderIf(includeName, () => (
        <Typography variant="body1" className={style.statusName}>
          {status.name}
          {renderIf(isSBPaymentUnconfirmed, () => (
            <span className={style.smallNameText}> (SB)</span>
          ))}
          {renderIf(isSBPaymentPending, () => (
            <span className={style.smallNameText}> ($)</span>
          ))}
        </Typography>
      ))}
      {renderIf(includeType, () => (
        <Typography variant="body1" className={style.statusType}>{typeOverride || status.statusType}</Typography>
      ))}
    </div>
  )
}

